/**
 * Biome Clustering System
 *
 * Creates cohesive 7-tile biome regions (1 center + 6 surrounding hexes)
 * with dominant (4-5 tiles) and secondary (2-3 tiles) biomes.
 *
 * Clusters spawn as complete units and reveal all at once when discovered.
 */

import { HEX_DIRECTIONS, hexDistance, coordsToId } from '../../utils/hexGrid';
import { BIOMES } from '../../data/world-map/biomes';
import { getBiomeSpawnWeights, selectRandomBiome } from '../../data/world-map/balance';

/**
 * Generate a complete 7-tile cluster pattern
 * @param {number} centerQ - Center tile Q coordinate
 * @param {number} centerR - Center tile R coordinate
 * @param {number} currentAge - Current player age/era
 * @returns {Object} Cluster definition with 7 tile assignments
 */
export function generateBiomeClusterPattern(centerQ, centerR, currentAge) {
  // Get valid biomes for this age (exclude special biomes)
  const weights = getBiomeSpawnWeights(currentAge, BIOMES);

  // Remove special biomes from cluster pool
  // These should spawn through their own dedicated systems, not in clusters
  delete weights.teleportBeacon;
  delete weights.kingdom;
  Object.keys(weights).forEach(biomeId => {
    const biome = BIOMES[biomeId];
    if (!biome) return;
    // Exclude: dungeons, towers, encounters, city-states/kingdoms
    if (biome.isDungeonBiome) delete weights[biomeId];
    if (biome.isTowerBiome) delete weights[biomeId];
    if (biome.isEncounterBiome) delete weights[biomeId];
    if (biome.isKingdomBiome) delete weights[biomeId];
  });

  // Select dominant and secondary biomes
  const dominant = selectRandomBiome(weights);
  delete weights[dominant]; // Ensure different biome
  const secondary = selectRandomBiome(weights);

  // Generate 7 tile positions (center + 6 surrounding)
  const tiles = [
    { q: centerQ, r: centerR } // Center tile
  ];

  // Add 6 surrounding hexes
  HEX_DIRECTIONS.forEach(dir => {
    tiles.push({
      q: centerQ + dir.q,
      r: centerR + dir.r
    });
  });

  // Assign biomes: 4-5 dominant, 2-3 secondary
  const dominantCount = 4 + Math.floor(Math.random() * 2); // 4 or 5
  const secondaryCount = 7 - dominantCount; // 3 or 2

  // Shuffle tiles and assign biomes
  const shuffledIndices = Array.from({ length: 7 }, (_, i) => i)
    .sort(() => Math.random() - 0.5);

  const tileAssignments = tiles.map((pos, index) => {
    const shuffledIndex = shuffledIndices[index];
    const biome = shuffledIndex < dominantCount ? dominant : secondary;

    return {
      q: pos.q,
      r: pos.r,
      nodeId: coordsToId(pos.q, pos.r),
      biome
    };
  });

  return {
    center: { q: centerQ, r: centerR },
    centerId: coordsToId(centerQ, centerR),
    dominant,
    secondary,
    dominantCount,
    secondaryCount,
    tiles: tileAssignments,
    created: Date.now()
  };
}

/**
 * Check if a cluster would conflict with existing discovered tiles
 * @param {Object} cluster - Cluster pattern from generateBiomeClusterPattern
 * @param {Object} discoveredNodes - Map of already discovered nodes
 * @returns {boolean} True if no conflicts, false if any tile already exists
 */
export function validateClusterPlacement(cluster, discoveredNodes) {
  // Check if ANY of the 7 tiles already exist
  for (const tile of cluster.tiles) {
    if (discoveredNodes[tile.nodeId]) {
      return false; // Conflict found
    }
  }
  return true; // No conflicts
}

/**
 * Check if a coordinate is part of any pending cluster
 * @param {number} q - Q coordinate
 * @param {number} r - R coordinate
 * @param {Object} pendingClusters - Map of pending clusters
 * @returns {Object|null} Cluster if found, null otherwise
 */
export function findClusterContainingTile(q, r, pendingClusters) {
  const nodeId = coordsToId(q, r);

  for (const cluster of Object.values(pendingClusters)) {
    if (cluster.tiles.some(tile => tile.nodeId === nodeId)) {
      return cluster;
    }
  }

  return null;
}

/**
 * Determine if a new cluster should spawn based on pity timer
 * @param {number} tilesDiscovered - Total tiles discovered
 * @param {number} lastClusterSpawnTile - Tile count when last cluster spawned
 * @returns {boolean} True if cluster should spawn
 */
export function shouldSpawnCluster(tilesDiscovered, lastClusterSpawnTile) {
  const tilesSinceLastCluster = tilesDiscovered - lastClusterSpawnTile;
  const spawnInterval = 10 + Math.floor(Math.random() * 6); // 10-15 tiles

  // Don't spawn clusters in early game
  if (tilesDiscovered < 25) return false;

  return tilesSinceLastCluster >= spawnInterval;
}

/**
 * Find a valid spawn location for a cluster near the frontier
 * @param {Object} discoveredNodes - Currently discovered nodes
 * @param {Object} pendingClusters - Pending clusters
 * @param {number} attempts - Max attempts to find valid location
 * @returns {Object|null} {q, r} coordinates or null if no valid location found
 */
export function findValidClusterSpawnPoint(discoveredNodes, pendingClusters, attempts = 100) {
  // Find frontier tiles and expand outward to find cluster spawn points
  const candidates = [];

  for (const [nodeId, node] of Object.entries(discoveredNodes)) {
    // Skip kingdom and special tiles
    if (node.isHub || node.isBeacon || BIOMES[node.biome]?.isDungeonBiome) {
      continue;
    }

    // Get coordinates from either node.q/r or node.coordinates.q/r
    const nodeQ = node.q ?? node.coordinates?.q;
    const nodeR = node.r ?? node.coordinates?.r;

    // BUGFIX: Skip nodes with invalid coordinates
    if (isNaN(nodeQ) || isNaN(nodeR) || nodeQ === undefined || nodeR === undefined) {
      console.error(`[Biome Cluster] BUGFIX: Discovered node has invalid coordinates:`, nodeId, node);
      continue;
    }

    // For each discovered tile, look 2-4 tiles away in all directions
    for (let distance = 2; distance <= 4; distance++) {
      // Try 6 directions (every 60 degrees around hex)
      for (let angle = 0; angle < 6; angle++) {
        const direction = HEX_DIRECTIONS[angle];

        // Calculate position at this distance
        const candidateQ = nodeQ + (direction.q * distance);
        const candidateR = nodeR + (direction.r * distance);

        // Check if this location is valid (not too close to kingdom)
        const distanceFromKingdom = hexDistance(0, 0, candidateQ, candidateR);
        if (distanceFromKingdom < 6) continue;

        // Check if not already discovered
        const candidateId = coordsToId(candidateQ, candidateR);
        if (discoveredNodes[candidateId]) continue;

        // Add to candidates if not duplicate
        if (!candidates.some(c => c.q === candidateQ && c.r === candidateR)) {
          candidates.push({ q: candidateQ, r: candidateR });
        }
      }
    }
  }

  console.log(`[Biome Cluster] Generated ${candidates.length} candidate spawn points, checking against ${Object.keys(pendingClusters).length} pending clusters`);

  // Log pending cluster tile IDs for debugging
  if (Object.keys(pendingClusters).length > 0) {
    for (const [key, cluster] of Object.entries(pendingClusters)) {
      const tileIds = cluster.tiles.map(t => t.nodeId).join(', ');
      console.log(`[Biome Cluster] Pending cluster "${key}" occupies: ${tileIds}`);
    }
  }

  // Shuffle and try candidates
  const shuffled = candidates.sort(() => Math.random() - 0.5);

  let discoveredConflicts = 0;
  let pendingConflicts = 0;

  for (let i = 0; i < Math.min(attempts, shuffled.length); i++) {
    const candidate = shuffled[i];

    // Validate candidate has valid coordinates
    if (isNaN(candidate.q) || isNaN(candidate.r)) {
      console.error(`[Biome Cluster] BUGFIX: Skipping candidate with NaN coordinates:`, candidate);
      continue;
    }

    // Build test cluster (7 tiles)
    const testCluster = {
      tiles: [
        { q: candidate.q, r: candidate.r, nodeId: coordsToId(candidate.q, candidate.r) },
        ...HEX_DIRECTIONS.map(dir => ({
          q: candidate.q + dir.q,
          r: candidate.r + dir.r,
          nodeId: coordsToId(candidate.q + dir.q, candidate.r + dir.r)
        }))
      ]
    };

    // Validate all 7 tiles are undiscovered
    if (!validateClusterPlacement(testCluster, discoveredNodes)) {
      discoveredConflicts++;
      continue;
    }

    // Check against pending clusters
    let conflictsWithPending = false;
    for (const pending of Object.values(pendingClusters)) {
      for (const tile of testCluster.tiles) {
        if (pending.tiles.some(t => t.nodeId === tile.nodeId)) {
          conflictsWithPending = true;
          break;
        }
      }
      if (conflictsWithPending) break;
    }

    if (conflictsWithPending) {
      pendingConflicts++;
      continue;
    }

    console.log(`[Biome Cluster] ✅ Found valid spawn point at (${candidate.q}, ${candidate.r}) after ${i + 1} attempts (discovered conflicts: ${discoveredConflicts}, pending conflicts: ${pendingConflicts})`);
    return { q: candidate.q, r: candidate.r };
  }

  console.log(`[Biome Cluster] ❌ Searched ${Math.min(attempts, shuffled.length)} candidates - ${discoveredConflicts} had discovered tile conflicts, ${pendingConflicts} had pending cluster conflicts`);
  return null; // No valid location found
}
